/**
 * SaveLoadManager - Handles game save and load operations
 */

import { World } from '@/core/ecs/World';
import { Serializer } from '@/core/save/Serializer';
import { Deserializer } from '@/core/save/Deserializer';
import { storageManager } from '@/core/save/StorageManager';
import { autoSave } from '@/core/save/AutoSave';
import type { SaveData } from '@/core/save/SaveData';
import type { FactionManager } from '@/game/factions/FactionManager';
import type { TechSystem } from '@/game/tech/TechSystem';
import type { VisionSystem } from '@/game/systems/VisionSystem';
import type { DiplomacySystem } from '@/game/systems/DiplomacySystem';
import type { AIController } from '@/game/ai/AIController';
import type { EventLog } from '@/ui/components/EventLog';
import type { FogRenderer } from '@/rendering/effects/FogRenderer';
import type { Minimap } from '@/ui/components/Minimap';
import { FACTION_POSITIONS } from './GameConstants';
import type { FactionId } from '@/types';

export interface SaveLoadCallbacks {
  getGameTime: () => number;
  setGameTime: (time: number) => void;
}

export class SaveLoadManager {
  private world: World;
  private factionManager: FactionManager;
  private techSystem: TechSystem;
  private visionSystem: VisionSystem;
  private diplomacySystem: DiplomacySystem;
  private aiController: AIController;
  private eventLog: EventLog;
  private fogRenderer: FogRenderer;
  private minimap: Minimap;
  private callbacks: SaveLoadCallbacks;

  constructor(
    world: World,
    factionManager: FactionManager,
    techSystem: TechSystem,
    visionSystem: VisionSystem,
    diplomacySystem: DiplomacySystem,
    aiController: AIController,
    eventLog: EventLog,
    fogRenderer: FogRenderer,
    minimap: Minimap,
    callbacks: SaveLoadCallbacks
  ) {
    this.world = world;
    this.factionManager = factionManager;
    this.techSystem = techSystem;
    this.visionSystem = visionSystem;
    this.diplomacySystem = diplomacySystem;
    this.aiController = aiController;
    this.eventLog = eventLog;
    this.fogRenderer = fogRenderer;
    this.minimap = minimap;
    this.callbacks = callbacks;
  }

  serializeGame(name: string = 'Quick Save'): SaveData {
    return Serializer.serialize(
      this.world,
      this.factionManager,
      this.techSystem,
      this.visionSystem,
      this.diplomacySystem,
      name
    );
  }

  async saveGame(slot: number, name: string): Promise<boolean> {
    try {
      const saveData = this.serializeGame(name);
      await storageManager.init();
      const success = await storageManager.save(slot, saveData);
      if (success) {
        this.eventLog.addEvent('保存成功', `游戏已保存到槽位 ${slot + 1}`, 'info');
      }
      return success;
    } catch (error) {
      console.error('Save failed:', error);
      this.eventLog.addEvent('保存失败', '无法保存游戏', 'info');
      return false;
    }
  }

  loadGame(saveData: SaveData): boolean {
    autoSave.stop();

    const success = Deserializer.deserialize(
      saveData,
      this.world,
      this.factionManager,
      this.techSystem,
      this.visionSystem,
      this.aiController
    );

    if (success) {
      const playerFaction = saveData.playerFaction;
      this.visionSystem.setPlayerFaction(playerFaction);
      this.fogRenderer.setPlayerFaction(playerFaction);
      this.callbacks.setGameTime(saveData.gameTime);

      const homePos = FACTION_POSITIONS[playerFaction as FactionId];
      this.minimap.setHomePosition(homePos.x, homePos.z);

      autoSave.setSerializeCallback(() => this.serializeGame('自动存档'));
      autoSave.start();

      this.eventLog.addEvent('读取成功', '游戏已加载', 'info');
      return true;
    } else {
      this.eventLog.addEvent('读取失败', '无法加载存档', 'info');
      return false;
    }
  }

  async quickSave(): Promise<void> {
    await this.saveGame(0, `快速存档 - ${new Date().toLocaleTimeString()}`);
  }

  async quickLoad(): Promise<void> {
    await storageManager.init();
    const saveData = await storageManager.load(0);
    if (saveData) {
      this.loadGame(saveData);
    } else {
      this.eventLog.addEvent('无存档', '槽位 1 没有存档', 'info');
    }
  }

  setupAutoSave(): void {
    autoSave.setSerializeCallback(() => this.serializeGame('自动存档'));
    autoSave.start();
  }

  dispose(): void {
    autoSave.dispose();
  }
}
